import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { Company, CompanyInput, CompanyStatus } from "./types";
import { COMPANY_STATUSES } from "./types";
import type { LeadType } from "@/lib/lead-type";
import { DEFAULT_STAGE } from "@/lib/stages";
import type { CompanySource } from "./types";
import {
  DEFAULT_COMPANY_SORT,
  type CompanySortState,
} from "./companies-list-sort";
import { makeContracts } from "@/features/crm-fields/contracts";
import { makeSupabaseBackend } from "@/features/crm-fields/supabase-backend";
import { normalizeCompanyName } from "@/lib/companies/match";
import { normalizeLeadSourceInput } from "@/lib/lead-sources";
import { softDeleteCompaniesForOrg } from "./soft-delete";

function relayFrom(table: string) {
  return (supabase as unknown as SupabaseClient).schema("relay").from(table);
}

const COMPANY_FIELDS = `
  id, organization_id, name, website, phone, email, industry,
  address_line1, address_line2, address_city, address_state,
  address_postal_code, address_country, owner_id, status, lead_type,
  primary_contact_id, platform_organization_id, source,
  lead_score, notes, metadata, created_at, updated_at, deleted_at
`;

interface CompaniesListFilter {
  status?: CompanyStatus | "all";
  leadType?: LeadType | "all";
  sort?: CompanySortState;
  fetchAllForClientFiltering?: boolean;
}

const COMPANIES_LIST_PAGE_SIZE = 500;

export function useCompaniesList(filter: CompaniesListFilter = {}) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["companies", "list", organizationId, filter],
    queryFn: async (): Promise<Company[]> => {
      if (!organizationId) return [];

      const buildQuery = () => {
        let query = relayFrom("companies")
          .select(COMPANY_FIELDS)
          .eq("organization_id", organizationId)
          .is("deleted_at", null);

        if (filter.status && filter.status !== "all") {
          query = query.eq("status", filter.status);
        }
        if (filter.leadType && filter.leadType !== "all") {
          query = query.eq("lead_type", filter.leadType);
        }

        const sort = filter.sort ?? DEFAULT_COMPANY_SORT;
        query = query.order(sort.field, {
          ascending: sort.direction === "asc",
          nullsFirst: false,
        });
        if (sort.field !== "name") {
          query = query.order("name", { ascending: true, nullsFirst: false });
        }
        return query;
      };

      if (filter.fetchAllForClientFiltering) {
        const rows: Company[] = [];

        for (let from = 0; ; from += COMPANIES_LIST_PAGE_SIZE) {
          const to = from + COMPANIES_LIST_PAGE_SIZE - 1;
          const { data, error } = await buildQuery().range(from, to);
          if (error) throw error;

          const page = (data ?? []) as Company[];
          rows.push(...page);

          if (page.length < COMPANIES_LIST_PAGE_SIZE) return rows;
        }
      }

      const { data, error } = await buildQuery().limit(COMPANIES_LIST_PAGE_SIZE);
      if (error) throw error;
      return (data ?? []) as Company[];
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCompany(companyId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["companies", "detail", organizationId, companyId],
    queryFn: async (): Promise<Company | null> => {
      if (!companyId || !organizationId) return null;
      const { data, error } = await relayFrom("companies")
        .select(COMPANY_FIELDS)
        .eq("id", companyId)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Company | null;
    },
    enabled: !!companyId && !!organizationId,
    staleTime: 30 * 1000,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: CompanyInput): Promise<Company> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await relayFrom("companies")
        .insert({ ...input, organization_id: organizationId })
        .select(COMPANY_FIELDS)
        .single();
      if (error) throw error;
      return data as Company;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

export function useUpdateCompany(companyId: string) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: CompanyInput): Promise<Company> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await relayFrom("companies")
        .update(input)
        .eq("id", companyId)
        .eq("organization_id", organizationId)
        .select(COMPANY_FIELDS)
        .single();
      if (error) throw error;
      return data as Company;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["companies", "list"] });
      queryClient.setQueryData<Company | null>(
        ["companies", "detail", organizationId, companyId],
        (prev) => (prev ? { ...prev, ...data } : null)
      );
    },
  });
}

export function useSoftDeleteCompany() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: (companyId: string) => softDeleteCompaniesForOrg(organizationId, [companyId]),
    retry: false,
    onSuccess: (_data, companyId) => {
      // Every company query, not just lists: the recent/search caches feed
      // CompanyPicker, which must not offer a deleted company.
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.removeQueries({
        queryKey: ["companies", "detail", organizationId, companyId],
      });
    },
  });
}

export function useRecentCompanies(limit = 10) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["companies", "recent", organizationId, limit],
    queryFn: async (): Promise<Company[]> => {
      if (!organizationId) return [];
      const { data, error } = await relayFrom("companies")
        .select(COMPANY_FIELDS)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .order("updated_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Company[];
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000,
  });
}

export function useCompanySearch(query: string, limit = 20) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["companies", "search", organizationId, query, limit],
    queryFn: async (): Promise<Company[]> => {
      if (!organizationId || query.trim().length === 0) return [];
      const { data, error } = await relayFrom("companies")
        .select(COMPANY_FIELDS)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .ilike("name", `%${query.trim()}%`)
        .order("name")
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Company[];
    },
    enabled: !!organizationId && query.trim().length > 0,
    staleTime: 10 * 1000,
  });
}

/** Contacts linked to a company (account-hub roster). */
export function useCompanyContacts(companyId: string | undefined) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["companies", "contacts", organizationId, companyId],
    queryFn: async () => {
      if (!companyId || !organizationId) return [];
      const { data, error } = await relayFrom("contacts")
        .select("id, first_name, last_name, email, job_title, status")
        .eq("organization_id", organizationId)
        .eq("company_id", companyId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        job_title: string | null;
        status: string;
      }>;
    },
    enabled: !!companyId && !!organizationId,
    staleTime: 30 * 1000,
  });
}

export interface ParsedCompanyRow {
  name?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  industry?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
  status?: string | null;
  source?: string | null;
  lead_score?: string | null;
  notes?: string | null;
  customFields?: Record<string, string>; // field_key -> raw value
  tags?: string[]; // slugs
}

export interface CompanyImportSummary {
  imported: number;
  skipped: number;
  errors: number;
  errorDetails: Record<string, string>;
}

function coerceCompanyStatus(v: string | null | undefined): CompanyStatus {
  const s = (v ?? "").trim().toLowerCase();
  return (COMPANY_STATUSES as readonly string[]).includes(s) ? (s as CompanyStatus) : DEFAULT_STAGE;
}
function coerceCompanySource(v: string | null | undefined): CompanySource {
  // source is free text (built-in token or custom). Canonicalize known
  // built-ins from CSV/user casing (e.g. "Referral", "Website") while
  // preserving unknown custom values as entered. Empty falls back to "import"
  // (auto-created/imported companies).
  return normalizeLeadSourceInput(v, "import");
}

export function useImportCompanies() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ rows }: { fileName: string; rows: ParsedCompanyRow[] }): Promise<CompanyImportSummary> => {
      if (!organizationId) throw new Error("No active organization");
      const contracts = makeContracts(makeSupabaseBackend(organizationId));

      let imported = 0;
      let skipped = 0;
      let errors = 0;
      const errorDetails: Record<string, string> = {};

      // Hoisted match map: fetch existing org companies ONCE, key by normalized name.
      const { data: existing, error: listErr } = await relayFrom("companies")
        .select("id, name")
        .eq("organization_id", organizationId)
        .is("deleted_at", null);
      if (listErr) throw listErr;
      const byName = new Map<string, string>();
      for (const c of (existing ?? []) as Array<{ id: string; name: string }>) {
        byName.set(normalizeCompanyName(c.name), c.id);
      }

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const name = (row.name ?? "").trim();
        if (!name) { skipped++; continue; }
        try {
          const norm = normalizeCompanyName(name);
          let companyId = byName.get(norm);
          if (companyId) {
            skipped++; // existing match — still gets fields/tags below
          } else {
            const { data: created, error: insErr } = await relayFrom("companies")
              .insert({
                organization_id: organizationId,
                name,
                website: row.website?.trim() || null,
                phone: row.phone?.trim() || null,
                email: row.email?.trim() || null,
                industry: row.industry?.trim() || null,
                address_line1: row.address_line1?.trim() || null,
                address_line2: row.address_line2?.trim() || null,
                address_city: row.address_city?.trim() || null,
                address_state: row.address_state?.trim() || null,
                address_postal_code: row.address_postal_code?.trim() || null,
                address_country: row.address_country?.trim() || null,
                status: coerceCompanyStatus(row.status),
                source: coerceCompanySource(row.source),
                lead_score:
                  row.lead_score == null || row.lead_score.trim() === ""
                    ? null
                    : Number.isFinite(Number(row.lead_score.trim()))
                      ? Number(row.lead_score.trim())
                      : null,
                notes: row.notes?.trim() || null,
              })
              .select("id")
              .single();
            if (insErr) throw insErr;
            companyId = (created as { id: string }).id;
            byName.set(norm, companyId);
            imported++;
          }

          for (const [field_key, raw] of Object.entries(row.customFields ?? {})) {
            if (raw == null || raw === "") continue;
            await contracts.setValue("company", companyId, field_key, raw);
          }
          for (const slug of row.tags ?? []) {
            if (!slug) continue;
            await contracts.ensureTag(slug, { name: slug });
            await contracts.applyTag(slug, "company", companyId);
          }
        } catch (e) {
          errors++;
          errorDetails[`row_${i + 1}`] = e instanceof Error ? e.message : String(e);
        }
      }
      return { imported, skipped, errors, errorDetails };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

/** Deals linked to a company (incl. company-only deals). */
export function useCompanyDeals(companyId: string | undefined) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["companies", "deals", organizationId, companyId],
    queryFn: async () => {
      if (!companyId || !organizationId) return [];
      const { data, error } = await relayFrom("deals")
        .select("id, title, value, currency, status, stage_id, pipeline:pipelines ( value_unit )")
        .eq("organization_id", organizationId)
        .eq("company_id", companyId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as Array<{
        id: string;
        title: string;
        value: number;
        currency: string;
        status: string;
        stage_id: string | null;
        pipeline:
          | { value_unit: string | null }
          | { value_unit: string | null }[]
          | null;
      }>).map((d) => ({
        ...d,
        // Embeds may come back as array or object (same quirk as deals/api).
        pipeline: Array.isArray(d.pipeline) ? (d.pipeline[0] ?? null) : d.pipeline,
      }));
    },
    enabled: !!companyId && !!organizationId,
    staleTime: 30 * 1000,
  });
}
